import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  formatPhoneNumber,
  parseApiResponse,
  userMessageForCode,
  loadCheckoutAttemptId,
  saveCheckoutAttemptId,
  clearCheckoutLocalState,
  checkoutStatusPath
} from '../utils/checkoutClient';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 255, 136, 0.3);
  border-radius: 20px;
  padding: 3rem;
  width: 100%;
  max-width: 600px;
  backdrop-filter: blur(10px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  text-align: center;
`;

const Title = styled.h1`
  font-size: 2.2rem;
  margin-bottom: 1rem;
  color: #00ff88;
  text-shadow: 0 0 10px rgba(0, 255, 136, 0.5);
`;

const Subtitle = styled.p`
  color: #cccccc;
  margin-bottom: 2rem;
  font-size: 1.05rem;
`;

const OrderDetails = styled.div`
  background: rgba(0, 255, 136, 0.1);
  border: 1px solid #00ff88;
  border-radius: 15px;
  padding: 1.5rem;
  margin: 2rem 0;
  text-align: left;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  color: #cccccc;
`;

const InfoBox = styled.div`
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid #00d4ff;
  border-radius: 10px;
  padding: 1rem;
  margin: 2rem 0;
  color: #00d4ff;
  font-size: 0.9rem;
  line-height: 1.5;
  text-align: left;
`;

const Button = styled.button`
  background: linear-gradient(45deg, #00ff88, #00cc6a);
  color: #000000;
  padding: 1rem 2rem;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  margin: 0.5rem;
`;

const STATUS_TITLE = {
  checkout_pending: 'Checkout in progress',
  payment_pending: 'Payment processing',
  payment_succeeded: 'Payment received',
  provisioning_pending: 'Activating number',
  provisioned: 'Number ready',
  provisioning_failed: 'Setup needs attention',
  canceled: 'Checkout canceled',
  expired: 'Checkout expired',
  refunded: 'Refunded'
};

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // URL may only carry correlation id — never status token
  const checkoutAttemptId =
    searchParams.get('checkoutAttemptId') || loadCheckoutAttemptId() || '';

  const [checkout, setCheckout] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    if (!checkoutAttemptId) {
      setLoading(false);
      setError(
        'Missing checkout reference. If you paid, contact support with your Stripe receipt — do not pay again.'
      );
      return;
    }
    saveCheckoutAttemptId(checkoutAttemptId);
    try {
      const res = await fetch(checkoutStatusPath(checkoutAttemptId), {
        method: 'GET',
        credentials: 'include'
      });
      const parsed = await parseApiResponse(res);
      if (parsed.parseError) {
        setError(
          'We could not load checkout status. Your payment may still have succeeded — refresh this page or contact support with your checkout reference. Do not pay again.'
        );
        setLoading(false);
        return;
      }
      if (res.status === 401) {
        setError(
          userMessageForCode(
            parsed.data?.code || 'UNAUTHORIZED',
            'Sign in is required to view this checkout. Arriving here from Stripe does not activate your account. If you already paid, do not pay again.'
          )
        );
        setLoading(false);
        return;
      }
      if (res.status === 404) {
        setError(
          userMessageForCode(
            parsed.data?.code || 'CHECKOUT_NOT_FOUND',
            'This checkout is unavailable or has expired. If you already paid, do not pay again — contact support with your checkout reference or Stripe receipt.'
          )
        );
        setLoading(false);
        return;
      }
      if (!parsed.ok || !parsed.data?.checkout) {
        setError(userMessageForCode(parsed.data?.code, parsed.data?.message));
        setLoading(false);
        return;
      }
      const c = parsed.data.checkout;
      setCheckout(c);
      setError('');
      setLoading(false);
    } catch {
      setError(
        'Temporary network issue while loading status. Refresh this page shortly. Do not start a new payment until status is confirmed.'
      );
      setLoading(false);
    }
  }, [checkoutAttemptId]);

  useEffect(() => {
    fetchStatus();
    const id = setInterval(() => {
      setCheckout((current) => {
        const st = current?.status;
        if (
          !st ||
          ['provisioned', 'provisioning_failed', 'canceled', 'expired', 'refunded'].includes(st)
        ) {
          return current;
        }
        fetchStatus();
        return current;
      });
    }, 4000);
    return () => clearInterval(id);
  }, [fetchStatus]);

  const status = checkout?.status || 'checkout_pending';
  const isProvisioned = status === 'provisioned';

  return (
    <Container>
      <Card>
        <Title>{STATUS_TITLE[status] || 'Checkout status'}</Title>
        <Subtitle>
          {isProvisioned
            ? 'Your Skylink number is active'
            : 'This page reflects server status only — arriving here from Stripe does not by itself confirm payment.'}
        </Subtitle>

        {loading && <InfoBox>Loading latest status…</InfoBox>}
        {error && (
          <InfoBox style={{ borderColor: '#ff4444', color: '#ff8888' }} role="alert">
            {error}
          </InfoBox>
        )}

        {checkout && (
          <OrderDetails>
            <DetailRow>
              <span>Status:</span>
              <span>{checkout.status}</span>
            </DetailRow>
            <DetailRow>
              <span>Inmate:</span>
              <span>{checkout.inmateName}</span>
            </DetailRow>
            <DetailRow>
              <span>Phone:</span>
              <span>{formatPhoneNumber(checkout.selectedPhoneNumber)}</span>
            </DetailRow>
            <DetailRow>
              <span>Amount:</span>
              <span>
                {typeof checkout.totalCents === 'number'
                  ? `$${(checkout.totalCents / 100).toFixed(2)}`
                  : '—'}
              </span>
            </DetailRow>
            <DetailRow>
              <span>Checkout ref:</span>
              <span style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>
                {checkout.checkoutAttemptId}
              </span>
            </DetailRow>
          </OrderDetails>
        )}

        {status === 'provisioning_failed' && (
          <InfoBox style={{ borderColor: '#ffc107', color: '#ffc107' }}>
            {userMessageForCode('PROVISIONING_FAILED')}
          </InfoBox>
        )}

        {(status === 'payment_pending' ||
          status === 'payment_succeeded' ||
          status === 'provisioning_pending' ||
          status === 'checkout_pending') && (
          <InfoBox>
            {userMessageForCode(
              status === 'payment_pending' ? 'PAYMENT_PENDING' : 'PROVISIONING_PENDING'
            )}{' '}
            This page will update automatically.
          </InfoBox>
        )}

        <div>
          <Button type="button" onClick={() => fetchStatus()}>
            Refresh status
          </Button>
          <Button
            type="button"
            onClick={() => {
              if (isProvisioned) clearCheckoutLocalState();
              navigate('/dashboard');
            }}
          >
            Dashboard
          </Button>
        </div>
      </Card>
    </Container>
  );
};

export default PaymentSuccess;
